import { type Currency } from '@webapp/core/entities/external/Currency'
import { PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { type ExpenseTypeFieldsApiValue } from '@webapp/modules/expense/data-access/api-models/ExpenseApiModel'
import { type ExpenseRepository } from '@webapp/modules/expense/data-access/repositories/ExpenseRepository'
import { type ReimbursementSettingsRepository } from '@webapp/modules/expense/data-access/repositories/ReimbursementSettingsRepository'
import { type RequestableCategoryRepository } from '@webapp/modules/expense/data-access/repositories/RequestableCategoryRepository'
import { type ExpenseFormReimbursementCustomization } from '@webapp/modules/expense/entities/enums'
import { type ExpensePaymentTypeEnum } from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import {
  EXPENSE_TYPE_NAME_BY_ENUM,
  type ExpenseTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { UnitsOfDistance } from '@webapp/modules/expense/entities/enums/UnitOfDistanceEnum'
import { ExpenseDraft } from '@webapp/modules/expense/entities/ExpenseDraft'
import {
  type ExpenseDraftItem,
  type ExpenseDraftItemAccount,
  type ExpenseDraftItemCreditCard,
} from '@webapp/modules/expense/entities/ExpenseDraft/ExpenseDraft'
import { type ExpenseTypeData } from '@webapp/modules/expense/entities/ExpenseItem/ExpenseTypeData'
import { type ISettingsRepository } from '@webapp/platform/finance'

// Picks the bucket by the item's `expense_type` enum rather than probing keys —
// legacy drafts can carry stale buckets for previously-selected types.
function parseExpenseTypeData(
  expenseType: ExpenseTypeEnum,
  fields: Record<string, unknown> | null
): ExpenseTypeData | null {
  const typeName = EXPENSE_TYPE_NAME_BY_ENUM[expenseType]
  if (typeName == null) return null

  if (typeName === 'OTHER' || typeName === 'LODGING') {
    return { type: typeName, data: null }
  }

  const bucket = fields?.[typeName] as ExpenseTypeFieldsApiValue | undefined
  if (bucket == null) return null

  switch (typeName) {
    case 'MEALS':
      return {
        type: 'MEALS',
        data: { tips: Number.parseFloat(bucket.tips?.value ?? '0') },
      }
    case 'MILEAGE':
      return {
        type: 'MILEAGE',
        data: {
          mileageRate: Number.parseFloat(bucket.mileage_rate?.value ?? '0'),
          distance: Number.parseFloat(bucket.distance?.value ?? '0'),
          unitOfDistance:
            bucket.unit_of_distance?.value === UnitsOfDistance.MILES
              ? UnitsOfDistance.MILES
              : UnitsOfDistance.KILOMETERS,
        },
      }
    case 'LABOUR':
      return {
        type: 'LABOUR',
        data: {
          hourlyRate: Number.parseFloat(bucket.hourly_rate?.value ?? '0'),
          hoursOfWork: Number.parseFloat(bucket.hours_of_work?.value ?? '0'),
        },
      }
    case 'AIRFARE':
      return {
        type: 'AIRFARE',
        data: {
          airline: bucket.airline?.value ?? '',
          ticketNumber: bucket.ticket_number?.value ?? '',
          departureDate: bucket.departure_date?.value || null,
          arrivalDate: bucket.arrival_date?.value || null,
        },
      }
  }
}

function inferTaxMode(
  taxAmount: number,
  taxPercentage: string,
  taxId: Nullable<number>
): 'percent' | 'dollar' | 'saved' {
  if (taxId != null && taxId > 0) return 'saved'
  if (taxAmount <= 0) return 'percent'
  const parsedPercentage = Number.parseFloat(taxPercentage)
  if (!Number.isNaN(parsedPercentage) && parsedPercentage > 0) return 'percent'
  return 'dollar'
}

export interface ExpenseFormInitialItemData {
  id: number
  merchant: string
  accountId: number
  account: ExpenseDraftItemAccount | null
  creditCardId: Nullable<number>
  creditCard: ExpenseDraftItemCreditCard | null
  currency: Currency
  reimbursable: boolean
  expenseType: ExpenseDraftItem['expenseType']
  paymentType: ExpensePaymentTypeEnum
  amount: number
  taxMode: 'percent' | 'dollar' | 'saved'
  taxValue: string
  taxAmount: number
  taxId: Nullable<number>
  taxName: string
  taxPercentage: string
  transactionDate: string
  attachment: string
  notes: string
  expenseTypeData: ExpenseTypeData | null
  mileageRatePresetId: Nullable<number>
  mileageRatePresetName: Nullable<string>
}

export interface ExpenseFormInitialData {
  draft: ExpenseDraft | null
  reimbursementSetting: ExpenseFormReimbursementCustomization
  isBudgetOverageEnabled: boolean
  defaultLocation: PrimaryCategory | null
  defaultDepartment: SecondaryCategory | null
  baseCurrency: Currency
  items: ExpenseFormInitialItemData[]
}

interface ExecuteParams {
  baseCurrency: Currency
}

/** @deprecated Use {@link ExpenseFormInitialData} instead */
export type GetExpenseRequestFormDataUseCaseResult = ExpenseFormInitialData

interface Dependency {
  expenseRepository: ExpenseRepository
  reimbursementSettingsRepository: ReimbursementSettingsRepository
  requestableCategoryRepository: RequestableCategoryRepository
  settingsRepository: ISettingsRepository
}

export class GetExpenseRequestFormDataUseCase {
  constructor(private readonly deps: Dependency) {}

  execute = async ({
    baseCurrency,
  }: ExecuteParams): Promise<ExpenseFormInitialData> => {
    const [
      draftConstructors,
      reimbursementSetting,
      financeSettings,
      locationsResponse,
    ] = await Promise.all([
      this.deps.expenseRepository.getDrafts(),
      this.deps.reimbursementSettingsRepository.get(),
      this.deps.settingsRepository.getFinanceSettings(),
      this.deps.requestableCategoryRepository.listRequestableLocations({
        pageSize: 999,
      }),
    ])

    const locationConstructors = locationsResponse.data

    const draft =
      draftConstructors.length > 0
        ? new ExpenseDraft(draftConstructors[0])
        : null

    let defaultLocation: PrimaryCategory | null = null
    let defaultDepartment: SecondaryCategory | null = null

    if (draft !== null) {
      const matchedLocation = locationConstructors.find(
        (loc) => loc.id === draft.locationId
      )
      if (matchedLocation) {
        defaultLocation = new PrimaryCategory(matchedLocation)
      }

      try {
        const { data: departmentConstructors } =
          await this.deps.requestableCategoryRepository.listRequestableDepartments(
            { branchId: draft.locationId, pageSize: 999 }
          )
        const matchedDepartment = departmentConstructors.find(
          (dept) => dept.id === draft.departmentId
        )
        if (matchedDepartment) {
          defaultDepartment = new SecondaryCategory(matchedDepartment)
        }
      } catch {
        defaultDepartment = null
      }
    } else if (locationConstructors.length > 0) {
      defaultLocation = new PrimaryCategory(locationConstructors[0])
      try {
        const { data: departmentConstructors } =
          await this.deps.requestableCategoryRepository.listRequestableDepartments(
            { branchId: defaultLocation.id, pageSize: 999 }
          )
        if (departmentConstructors.length > 0) {
          defaultDepartment = new SecondaryCategory(departmentConstructors[0])
        }
      } catch {
        defaultDepartment = null
      }
    }

    const items: ExpenseFormInitialItemData[] = draft
      ? draft.items.map((item) => ({
          id: item.id,
          merchant: item.merchant,
          accountId: item.accountId,
          account: item.account,
          creditCardId: item.creditCardId,
          creditCard: item.creditCard,
          currency: item.currency,
          reimbursable: item.reimbursable,
          expenseType: item.expenseType,
          paymentType: item.paymentType as ExpensePaymentTypeEnum,
          amount: item.amount,
          taxMode: inferTaxMode(item.taxAmount, item.taxPercentage, item.taxId),
          taxValue:
            inferTaxMode(item.taxAmount, item.taxPercentage, item.taxId) ===
            'percent'
              ? String(Number.parseFloat(item.taxPercentage || '0') * 100)
              : String(item.taxAmount),
          taxAmount: item.taxAmount,
          taxId: item.taxId,
          taxName: item.taxName,
          taxPercentage: item.taxPercentage,
          transactionDate: item.transactionDate,
          attachment: item.attachment,
          notes: item.notes,
          expenseTypeData: parseExpenseTypeData(
            item.expenseType,
            item.expenseTypeFields
          ),
          mileageRatePresetId: item.mileageRatePresetId,
          mileageRatePresetName: item.mileageRatePresetName,
        }))
      : []

    return {
      draft,
      reimbursementSetting,
      isBudgetOverageEnabled: financeSettings.data.isBudgetOverageEnabled,
      defaultLocation,
      defaultDepartment,
      baseCurrency,
      items,
    }
  }
}
